import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import editIcon from "../assets/icons/edit.svg";
import logoutIcon from "../assets/icons/logout.svg";
import homeIcon from "../assets/icons/home.svg";
import chevDownIcon from "../assets/icons/chevron-down.svg";

import logo from "../assets/images/max-energy-logo.png";

const HeaderBar = ({ data }) => {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const menuRef = useRef(null);

    const clientDetails = data?.data?.clientDetails || {};
    const displayName = clientDetails.userDisplayName || clientDetails.userName || "User";
    const email = clientDetails.userEmail || "";
    const initials = displayName.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);
    const clientName = clientDetails.clientName;
    const isAdmin = clientDetails.isAdmin === true;

    const homeRoute = isAdmin ? "/home/admin" : "/home/dashboard";

    // Close dropdown on outside click
    useEffect(() => {
        const handler = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const handleHome = () => {
        navigate(homeRoute, { state: { data } });
        setOpen(false);
    };

    const handleProfile = () => {
        navigate("/home/profile", { state: { data } });
        setOpen(false);
    };

    const handleCreateLogin = () => {
        navigate("/home/createlogin", { state: { data } });
        setOpen(false);
    };

    const handleLogout = () => {
        navigate("/");
        setOpen(false);
    };

    return (
        <div style={styles.header}>

            {/* ── Left: Logo ── */}
            <div style={styles.left}>
                <img src={logo} alt="MaxEnergy" style={styles.logo} />
                <span style={styles.portalTag}>Client Portal</span>
            </div>

            {/* ── Center: Client Name ── */}
            {
                isAdmin ? (
                    <div style={styles.center}>
                        <span style={styles.adminConsoleBadge}>
                            <i className="pi pi-shield" style={{ fontSize: 14 }} />
                            Administration Console
                        </span>
                    </div>
                ) : (
                    <div style={styles.center}>
                        <span style={styles.portalTag}>{clientName}</span>
                    </div>
                )
            }


            {/* ── Right: Home button + divider + profile dropdown ── */}
            <div style={styles.right}>

                {/* Home button */}
                <button style={styles.homeBtn} onClick={handleHome}>
                    <img src={homeIcon} alt="" width={14} height={14} />
                    Home
                </button>

                <div style={styles.divider} />

                {/* Profile dropdown */}
                <div ref={menuRef} style={{ position: "relative" }}>
                    <div style={styles.profileTrigger} onClick={() => setOpen(o => !o)}>
                        <div style={styles.avatar}>{initials}</div>
                        <img
                            src={chevDownIcon}
                            alt=""
                            width={13}
                            height={13}
                            style={{
                                opacity: 0.5,
                                transition: "transform 0.15s ease",
                                transform: open ? "rotate(180deg)" : "rotate(0deg)",
                            }}
                        />
                    </div>

                    {open && (
                        <div style={styles.dropdown}>
                            {/* Dropdown header */}
                            <div style={styles.dropdownHeader}>
                                <div style={styles.dropdownName}>{displayName}</div>
                                {email && <div style={styles.dropdownEmail}>{email}</div>}
                            </div>

                            <button style={styles.dropdownItem} onClick={handleHome}>
                                <img src={homeIcon} alt="" width={14} height={14} /> Dashboard
                            </button>
                            <button style={styles.dropdownItem} onClick={handleProfile}>
                                <img src={editIcon} alt="" width={14} height={14} /> Edit Profile
                            </button>

                            {/* Create Login — admin only */}
                            {isAdmin && (
                                <button style={styles.dropdownItem} onClick={handleCreateLogin}>
                                    <i className="pi pi-user-plus" style={{ fontSize: 14, width: 14 }} />
                                    Create Client Login
                                </button>
                            )}

                            <div style={styles.dropdownDivider} />

                            <button style={{ ...styles.dropdownItem, ...styles.dropdownItemDanger }} onClick={handleLogout}>
                                <img src={logoutIcon} alt="" width={14} height={14} /> Logout
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const styles = {
    header: {
        position: "sticky",
        top: 0,
        zIndex: 100,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 28px",
        height: 70,
        background: "#ffffff",
        borderBottom: "3px solid #4CAF50",
        boxShadow: "0 1px 6px rgba(0,0,0,0.07)",
    },

    /* ── Left ── */
    left: {
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 2,
        cursor: "pointer",
        userSelect: "none",
        textDecoration: "none",
    },
    logo: {
        height: 32,
        width: "auto",
        display: "block",
    },
    portalTag: {
        fontSize: 18,
        fontWeight: 600,
        color: "#4CAF50",
        letterSpacing: "0.10em",
        textTransform: "uppercase",
        lineHeight: 1,
    },

    /* ── center ── */
    center: {
        display: "flex",
        alignItems: "center",
        gap: 12,
    },
    clientName: {
        fontFamily: "var(--font-sans)",
        fontSize: 15,
        fontWeight: 600,
        color: "#374151",
        letterSpacing: "0.02em",
    },
    adminConsoleBadge: {
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        padding: "6px 16px",
        background: "#F0FDF4",
        border: "1px solid #BBF7D0",
        borderRadius: 99,
        fontSize: 14,
        fontWeight: 600,
        color: "#166534",
        fontFamily: "var(--font-sans)",
        letterSpacing: "0.02em",
        boxShadow: "0 1px 3px rgba(76,175,80,0.10)",
    },

    /* ── Right ── */
    right: {
        display: "flex",
        alignItems: "flex-end",
        gap: 12,
    },
    divider: {
        width: 1,
        height: 28,
        background: "#E5E7EB",
        marginBottom: 7,
        marginLeft: 5,
    },

    /* Home button */
    homeBtn: {
        display: "flex",
        alignItems: "center",
        gap: 6,
        padding: "6px 14px",
        marginBottom: 5,
        borderRadius: 6,
        border: "1px solid #D1FAE5",
        background: "#F0FDF4",
        color: "#166534",
        fontSize: 16,
        fontWeight: 500,
        cursor: "pointer",
        fontFamily: "inherit",
        transition: "background 0.15s ease, border-color 0.15s ease",
    },

    /* Profile trigger */
    profileTrigger: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "5px 10px",
        borderRadius: 6,
        cursor: "pointer",
        fontFamily: "inherit",
        transition: "background 0.15s ease",
    },
    avatar: {
        width: 40,
        height: 40,
        borderRadius: "50%",
        background: "#4CAF50",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 11,
        fontWeight: 700,
        color: "#fff",
        flexShrink: 0,
    },

    /* Dropdown */
    dropdown: {
        position: "absolute",
        top: "calc(100% + 8px)",
        right: 0,
        background: "#fff",
        border: "1px solid #E5E7EB",
        borderRadius: 10,
        boxShadow: "0 8px 24px rgba(0,0,0,0.10)",
        minWidth: 200,
        zIndex: 1000,
        overflow: "hidden",
    },
    dropdownHeader: {
        padding: "12px 16px",
        borderBottom: "1px solid #F3F4F6",
    },
    dropdownName: {
        fontSize: 14,
        fontWeight: 600,
        color: "#111827",
    },
    dropdownEmail: {
        fontSize: 12,
        color: "#9CA3AF",
        marginTop: 2,
    },
    dropdownAdminTag: {
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        marginTop: 6,
        padding: "2px 8px",
        background: "#FEF3C7",
        border: "1px solid #FDE68A",
        borderRadius: 99,
        fontSize: 11,
        fontWeight: 600,
        color: "#92400E",
        fontFamily: "var(--font-sans)",
    },
    dropdownItem: {
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "10px 16px",
        width: "100%",
        textAlign: "left",
        fontSize: 13,
        color: "#374151",
        background: "transparent",
        border: "none",
        cursor: "pointer",
        fontFamily: "inherit",
        transition: "background 0.12s ease",
    },
    dropdownItemDanger: {
        color: "#EF4444",
    },
    dropdownDivider: {
        height: 1,
        background: "#F3F4F6",
        margin: "2px 0",
    },
};

export default HeaderBar;
